import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { getLocationDetails } from '../services/api';
import { locationDetailsCache, locationDetailsInFlight } from '../services/locationDetailsCache';

/**
 * Does this outlet capture a service charge? Drives whether the Service Charge KPI, the
 * Financial Breakdown column and the EOD waterfall line render at all (hidden — never $0.00).
 *
 * Read off GET /merchants/location/{id}/details → `uiFeatureFlags.isServiceChargeEnabled`.
 * Missing / null / non-true → false, so nothing flashes in while the details call is loading.
 */
export function readServiceChargeEnabled(payload: any): boolean {
    const flags = payload?.uiFeatureFlags;
    return flags?.isServiceChargeEnabled === true;
}

const findToken = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['accessToken', 'access_token', 'token']) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

/**
 * Shares the location-details cache / in-flight map with BranchContext, DesktopLayout and
 * OutletDropdown, so this adds no extra network call.
 */
export function useServiceChargeEnabled(): boolean {
    const { auth } = useAuth();
    const { selectedBranch: branch } = useBranch();
    const locationId = (branch as any)?.id ?? (branch as any)?.locationId ?? (auth as any)?.locationId ?? (auth as any)?.location_id;
    const token = findToken(auth);
    const cacheKey = locationId ? String(locationId) : '';

    const [enabled, setEnabled] = useState<boolean>(() => readServiceChargeEnabled(cacheKey ? locationDetailsCache.get(cacheKey) : null));

    useEffect(() => {
        if (!cacheKey) { setEnabled(false); return; }
        const cached = locationDetailsCache.get(cacheKey);
        if (cached) { setEnabled(readServiceChargeEnabled(cached)); return; }
        if (!token) { setEnabled(false); return; }

        let cancelled = false;
        const inFlight = locationDetailsInFlight.get(cacheKey);
        const req = inFlight ?? getLocationDetails(locationId, token)
            .then((res: any) => {
                const payload = res?.metaDataInfo?.data ?? res?.data ?? res;
                if (payload) locationDetailsCache.set(cacheKey, payload);
                return payload;
            })
            .finally(() => { locationDetailsInFlight.delete(cacheKey); });
        if (!inFlight) locationDetailsInFlight.set(cacheKey, req);

        req
            .then((payload) => { if (!cancelled) setEnabled(readServiceChargeEnabled(payload)); })
            .catch(() => { if (!cancelled) setEnabled(false); });
        return () => { cancelled = true; };
    }, [cacheKey, token, locationId]);

    return enabled;
}
